import React, { useState, useEffect, useContext, useCallback } from 'react';
import { 
  Search, 
  User, 
  ArrowDownCircle,
  ArrowUpCircle,
  History,
  AlertCircle
} from 'lucide-react';
import { WarehouseContext } from '../../Utility/UserContext';
import { getAdvanceReport, getUsers } from '../../Utility/Api';
import { cn } from '../../lib/utils';

const AdvanceReport = () => {
  const { shopItem, subCompany } = useContext(WarehouseContext);
  const [advances, setAdvances] = useState([]);
  const [loading, setLoading] = useState(false);
  const [employees, setEmployees] = useState([]);
  const [filterEmployee, setFilterEmployee] = useState('');

  const fetchAdvances = useCallback(async () => {
    if (!shopItem || !subCompany) return;
    setLoading(true);
    try {
      const response = await getAdvanceReport({
        shop_id: subCompany.id,
        branch_id: shopItem.id,
        employee_id: filterEmployee
      });
      if (response.data && response.data.status === 'success') {
        setAdvances(response.data.data);
      }
    } catch (error) {
      console.error('Error fetching advances:', error);
    } finally {
      setLoading(false);
    }
  }, [shopItem, subCompany, filterEmployee]);

  useEffect(() => {
    fetchAdvances();
  }, [fetchAdvances]);

  useEffect(() => {
    const fetchEmployees = async () => {
      if (shopItem?.id) {
        const response = await getUsers(1, "", shopItem.id);
        if (response.data && response.data.data) {
          setEmployees(response.data.data);
        }
      }
    };
    fetchEmployees();
  }, [shopItem]);

  return (
    <div className="p-6 space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-slate-800">Employee Advance Report</h1>
          <p className="text-sm text-slate-500">Track pending advance balances and deduction history</p>
        </div>
      </div>

      {/* Filters */}
      <div className="bg-white p-4 rounded-xl border border-slate-200 shadow-sm flex items-end gap-4">
        <div className="w-64 space-y-1.5">
          <label className="text-[10px] font-bold text-slate-400 uppercase tracking-wider flex items-center gap-1">
            <User className="w-3 h-3" /> Filter by Employee
          </label>
          <select 
            value={filterEmployee}
            onChange={(e) => setFilterEmployee(e.target.value)}
            className="w-full bg-slate-50 border border-slate-200 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-blue-500/10 focus:border-blue-500 transition-all"
          >
            <option value="">All Employees</option>
            {employees.map(emp => (
              <option key={emp.user_Id} value={emp.user_Id}>{emp.full_name}</option>
            ))}
          </select>
        </div>
        <button 
          onClick={fetchAdvances}
          className="p-2.5 bg-blue-50 text-blue-600 rounded-lg hover:bg-blue-100 transition-colors"
        >
          <Search className="w-5 h-5" />
        </button>
      </div>

      <div className="grid grid-cols-1 gap-6">
        {loading ? (
          <div className="h-60 flex flex-col items-center justify-center text-slate-400 gap-3">
            <div className="w-8 h-8 border-4 border-blue-500 border-t-transparent rounded-full animate-spin"></div>
            <p className="text-sm font-medium">Generating report...</p>
          </div>
        ) : advances.length === 0 ? (
          <div className="h-60 bg-white rounded-xl border border-dashed border-slate-200 flex flex-col items-center justify-center text-slate-400 gap-2">
            <AlertCircle className="w-8 h-8 opacity-20" />
            <p className="text-sm font-medium italic">No advance records found for this location.</p>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-6">
            {advances.map((adv) => (
              <div key={adv.id} className="bg-white rounded-2xl border border-slate-200 shadow-sm overflow-hidden flex flex-col group hover:border-blue-200 hover:shadow-md transition-all">
                <div className="p-5 border-b border-slate-50 bg-slate-50/50">
                  <div className="flex items-start justify-between mb-4">
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 rounded-xl bg-white shadow-sm border border-slate-100 flex items-center justify-center text-blue-600 font-black">
                        {adv.employee_name?.charAt(0)}
                      </div>
                      <div>
                        <h3 className="font-bold text-slate-800">{adv.employee_name}</h3>
                        <p className="text-[10px] text-slate-400 font-bold uppercase tracking-wider">Employee ID: {adv.employee}</p>
                      </div>
                    </div>
                    {adv.pending_balance > 0 ? (
                      <div className="px-2 py-1 rounded-md bg-red-50 text-red-600 text-[10px] font-black uppercase tracking-tighter border border-red-100">
                        Pending
                      </div>
                    ) : (
                      <div className="px-2 py-1 rounded-md bg-green-50 text-green-600 text-[10px] font-black uppercase tracking-tighter border border-green-100">
                        Cleared
                      </div>
                    )}
                  </div>

                  <div className="flex flex-wrap gap-2">
                    <div className="flex-1 min-w-[80px] bg-white p-2 rounded-lg border border-slate-100">
                      <p className="text-[9px] font-bold text-slate-400 uppercase mb-1 whitespace-nowrap">Total Given</p>
                      <p className="text-sm font-black text-slate-700">₹{adv.total_given}</p>
                    </div>
                    <div className="flex-1 min-w-[80px] bg-white p-2 rounded-lg border border-slate-100">
                      <p className="text-[9px] font-bold text-green-400 uppercase mb-1 whitespace-nowrap">Deducted</p>
                      <p className="text-sm font-black text-green-600">₹{adv.total_deducted}</p>
                    </div>
                    <div className="flex-1 min-w-[80px] bg-white p-2 rounded-lg border border-blue-100 bg-blue-50/20">
                      <p className="text-[9px] font-bold text-blue-400 uppercase mb-1 whitespace-nowrap">Remaining</p>
                      <p className="text-sm font-black text-blue-700">₹{adv.pending_balance}</p>
                    </div>
                  </div>
                </div>

                {/* Progress Bar */}
                <div className="h-1.5 w-full bg-slate-100">
                  <div 
                    className="h-full bg-blue-600 transition-all duration-500" 
                    style={{ width: `${(adv.total_deducted / adv.total_given) * 100}%` }}
                  ></div>
                </div>

                <div className="p-4 flex-1 flex flex-col justify-between">
                  <div className="flex items-center justify-between text-xs text-slate-500 font-medium">
                    <span className="flex items-center gap-1"><History className="w-3.5 h-3.5" /> Last updated:</span>
                    <span>{new Date(adv.update_date).toLocaleDateString()}</span>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default AdvanceReport;
